import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  XCircle,
  ExternalLink,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackOrder = () => {
  const [searchParams] = useSearchParams();
  const initialOrderId = searchParams.get('id') || '';
  
  const [orderId, setOrderId] = useState(initialOrderId);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleTrack = async (e) => {
    e?.preventDefault();
    if (!orderId.trim()) {
      toast.error('Digite o número do pedido');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const response = await axios.get(`${API}/track/${orderId.trim().toUpperCase()}`);
      setTracking(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Pedido não encontrado. Verifique o número.');
      } else {
        toast.error('Erro ao buscar pedido');
      }
      setTracking(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      processing: { label: 'Processando', icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
      confirmed: { label: 'Confirmado', icon: CheckCircle, color: 'text-blue-500', bg: 'bg-blue-500/10' },
      preparing: { label: 'Preparando', icon: Package, color: 'text-purple-500', bg: 'bg-purple-500/10' },
      shipped: { label: 'Enviado', icon: Truck, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
      in_transit: { label: 'Em Trânsito', icon: Truck, color: 'text-blue-500', bg: 'bg-blue-500/10' },
      out_for_delivery: { label: 'Saiu para Entrega', icon: MapPin, color: 'text-orange-500', bg: 'bg-orange-500/10' },
      delivered: { label: 'Entregue', icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10' },
      cancelled: { label: 'Cancelado', icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10' }
    };
    return statusMap[status] || statusMap.processing;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      paid: 'Pago',
      failed: 'Falhou'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusInfo = tracking ? getStatusInfo(tracking.order_status) : null;
  const StatusIcon = statusInfo?.icon || Clock;

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="track-order-page">
      <div className="container-luxury max-w-2xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl mb-4">Rastrear Pedido</h1>
          <p className="text-muted-foreground">
            Digite o número do seu pedido para acompanhar o status
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleTrack} className="mb-12">
          <div className="flex gap-4">
            <Input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value.toUpperCase())}
              placeholder="Ex: A1B2C3D4"
              className="input-luxury text-lg uppercase tracking-wider"
              data-testid="track-order-input"
            />
            <Button 
              type="submit" 
              className="btn-primary px-8"
              disabled={loading}
              data-testid="track-order-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Rastrear
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Results */}
        {searched && tracking && (
          <div className="space-y-8 animate-fade-in-up">
            {/* Status Card */}
            <div className="bg-card border border-white/10 p-8 text-center">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${statusInfo.bg} mb-6`}>
                <StatusIcon className={`w-10 h-10 ${statusInfo.color}`} />
              </div>
              
              <h2 className="font-serif text-2xl mb-2">Pedido #{tracking.id}</h2>
              <p className={`text-xl font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </p>
              
              <div className="grid grid-cols-2 gap-6 mt-8 pt-8 border-t border-white/10">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Pagamento</p>
                  <p className="font-medium">{getPaymentStatusLabel(tracking.payment_status)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Previsão de Entrega</p>
                  <p className="font-medium">{tracking.estimated_delivery}</p>
                </div>
              </div>
            </div>

            {/* Tracking Code */}
            {tracking.tracking_code && (
              <div className="bg-card border border-white/10 p-6">
                <h3 className="font-serif text-lg mb-4">Código de Rastreio</h3>
                <div className="flex items-center justify-between">
                  <code className="text-primary text-lg font-mono">
                    {tracking.tracking_code}
                  </code>
                  {tracking.tracking_url && (
                    <a 
                      href={tracking.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Rastrear na Transportadora
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            {tracking.tracking_history && tracking.tracking_history.length > 0 && (
              <div className="bg-card border border-white/10 p-6">
                <h3 className="font-serif text-lg mb-6">Histórico</h3>
                <div className="space-y-0">
                  {tracking.tracking_history.slice().reverse().map((entry, index) => {
                    const entryInfo = getStatusInfo(entry.status);
                    const EntryIcon = entryInfo.icon;
                    const isLatest = index === 0;
                    
                    return (
                      <div key={index} className="flex gap-4">
                        {/* Timeline line */}
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isLatest ? entryInfo.bg : 'bg-white/5'}`}>
                            <EntryIcon className={`w-5 h-5 ${isLatest ? entryInfo.color : 'text-muted-foreground'}`} />
                          </div>
                          {index < tracking.tracking_history.length - 1 && (
                            <div className="w-0.5 h-12 bg-white/10" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 pb-8">
                          <p className={`font-medium ${isLatest ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {entry.description}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {formatDate(entry.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Initial status if no history */}
            {(!tracking.tracking_history || tracking.tracking_history.length === 0) && (
              <div className="bg-card border border-white/10 p-6">
                <h3 className="font-serif text-lg mb-4">Histórico</h3>
                <div className="flex gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${statusInfo.bg}`}>
                    <Clock className={`w-5 h-5 ${statusInfo.color}`} />
                  </div>
                  <div>
                    <p className="font-medium">Pedido recebido</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatDate(tracking.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Not found */}
        {searched && !tracking && !loading && (
          <div className="text-center py-12 animate-fade-in-up">
            <XCircle className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
            <h2 className="font-serif text-2xl mb-2">Pedido não encontrado</h2>
            <p className="text-muted-foreground">
              Verifique se o número do pedido está correto e tente novamente.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
